/* eslint-disable complexity */
/* eslint-disable max-lines-per-function */
import { get } from "lodash";
import moment from "moment";
import React from "react";
import { getActivityFeed_activityFeed as ActivityFeedType } from "types/gql-types";
import MessagePreviewItem from "./feed-item/message-preview-item";
import LetterFeedItem from "./feed-item/letter";
import DocumentFeedItem from "./feed-item/document";
import { NotDuplicateFeedItem } from "./feed-item/duplicate";
import activityStyles from "./activity-item.module.css";
import { styles } from "./activity-item-styles";

export const capitalize = (string: string) => string.charAt(0).toUpperCase() + string.slice(1);

const ACTIVITY_NAMES = {
  CREATE_DOCUMENT: "create_document",
  REMOVE_DOCUMENT: "remove_document",
  ARCHIVE_DOCUMENT: "archive_document",
  SENT_SMS: "sent_sms",
  SENT_LETTER_VIA_EMAIL: "sent_letter_via_email",
  SENT_LETTER: "sent_letter",
  SENT_EMAIL: "sent_email",
  SENT_EMAIL_FAILED: "sent_email_failed",
  SMS_FAILED: "sent_sms_failed",
  LETTER_FAILED: "sent_letter_failed",
  REMOVAL_FAILURE: "removal_notice_failure",
  REMOVAL_NOTICE: "removal_notice",
  WAITLIST_FAILURE: "annual_update_message_failure",
  CREATE_NOT_DUPLICATE: "create_not_duplicate",
};

const REMOVAL_AND_WAITLIST_NAMES = [
  ACTIVITY_NAMES.REMOVAL_FAILURE,
  ACTIVITY_NAMES.REMOVAL_NOTICE,
  ACTIVITY_NAMES.WAITLIST_FAILURE,
];

const FeedItem:React.FC<{
  data:ActivityFeedType,
  interestId:string
}> = ({ interestId, data }) => {
  const act = data;
  const diffMap = act.metadata?.updateDiff;
  const { metadata } = act;
  const errorsMap = act.metadata?.errorsMap;

  const renderPreviewForWaitlist = () => {
    if (!metadata) return null;
    const { body, messageType } = metadata;
    if (body && messageType !== "email") {
      return <MessagePreviewItem activity={act} />;
    }
    return null;
  };

  return (
    <div className="clearfix" style={styles.activityContainer}>
      <div style={styles.iconCont}>
        <div style={styles.iconCircle}>
          <div className="fa fa-pencil" style={styles.icon} />
        </div>
      </div>
      <div style={styles.activityCont}>
        <div style={styles.title}>{act.title}</div>

        {!!act.note && (<pre className={activityStyles.noteContents}>{act.note}</pre>)}

        {act.name === ACTIVITY_NAMES.CREATE_NOT_DUPLICATE && (
          <NotDuplicateFeedItem metadata={act.metadata} interestId={interestId} />
        )}

        {diffMap && (
          <div style={styles.diffContainer}>
            {Object.entries(diffMap).map(([keyName, diff]: [string, any], i) => {
              if (diff.removed === false && diff.added === null) return null;
              return (
                <div key={i} style={{ marginBottom: 3 }}>
                Changed field <span style={styles.diffField}>{keyName}</span>&ensp;
                  {/* string interp. ensures value like bool and nil are rendered */}
                from: <span style={styles.diffItem}>{`${diff.removed}`}</span> to:&ensp;
                  <span style={styles.diffItem}>{`${diff.added}`}</span>
                </div>
              );
            })}
          </div>
        )}

        {errorsMap && (
          <div style={styles.diffContainer}>
            {Object.entries(errorsMap).map(([keyName, value]: [string, any], i) => (
              <div key={i} style={{ marginBottom: 3 }}>
                Failed to map field <span style={styles.diffField}>{keyName}</span>&ensp;
                with the value of: <span style={styles.diffItem}>{`${JSON.stringify(value)}`}</span>
              </div>
            ))}
          </div>
        )}

        {act.name === ACTIVITY_NAMES.SENT_EMAIL && act.message && (
          <div>
            <div>
              To: {`${act.message.recipientName} (${act.message.emailToAddress})` ||
                act.metadata?.to}
            </div>
            {act.message.emailCcAddress && (<div>CC: {act.message.emailCcAddress}</div>)}
            <div>Status: {act.message.status || (act.name === "sent_email_failed" ? "Failed" : "Unknown")}</div>
            {["error", "bounced"].includes(act.message?.status ?? "") && (
              <div>Error message: {get(data, "message.error.message")}</div>
            )}
          </div>
        )}

        {act.name === ACTIVITY_NAMES.SENT_EMAIL_FAILED && (act.message ? (
          <div>
            <div>
              <span style={styles.bold}>To:</span>
              &nbsp;{`${act.message.recipientName} (${act.message.emailToAddress})`}
            </div>
            {act.message.emailCcAddress && (<div>CC: {act.message.emailCcAddress}</div>)}
            {["error", "bounced"].includes(act.message?.status ?? "") ? (
              <div>Error message: {get(data, "message.error.message")}</div>
            ) : (<div>Status: {act.message.status || "Failed"}</div>)}
          </div>
        ) : (
          <div>
            <div>
              Email sent to: {act.metadata?.to || "Blank"}
            </div>
            <div><span style={styles.bold}>Status:</span> Failed</div>
            {!metadata?.emailSubject && act.metadata?.body && <div>
              <span style={styles.bold}>Message:</span> {act.metadata.body}
            </div>}
          </div>
        ))}

        {metadata?.reason && (
          <div>
            <span style={styles.bold}>Reason:</span> {metadata.reason}
          </div>
        )}
        {metadata?.emailSubject && <MessagePreviewItem activity={act} />}


        {act.name === ACTIVITY_NAMES.SENT_LETTER_VIA_EMAIL && (
          <div>
            Email sent to: {metadata?.to} <br />
            <a target="_blank" href={`/api/attachments/audit/${act.id}`} rel="noreferrer">
              View Attachment
            </a>
          </div>
        )}

        {REMOVAL_AND_WAITLIST_NAMES.includes(act.name ?? "") && renderPreviewForWaitlist()}

        {[ACTIVITY_NAMES.CREATE_DOCUMENT, ACTIVITY_NAMES.REMOVE_DOCUMENT, ACTIVITY_NAMES.ARCHIVE_DOCUMENT].includes(act.name ?? "") && (
          <div>
            <DocumentFeedItem activity={act} />
          </div>
        )}

        {act.name === ACTIVITY_NAMES.SENT_SMS && act.message && (
          <div>
            <div>
              SMS sent to: {act.message.recipientName} ({act.message.mobileNumber})
            </div>
            <div>Status: {act.message.status}</div>
            {["error", "bounced"].includes(act.message?.status ?? "") && (
              <div>Error message: {get(data, "message.error.message")}</div>
            )}
            <div>Message: {act.message.body}</div>
          </div>
        )}

        {act.name === ACTIVITY_NAMES.SMS_FAILED && (<div>
          <div>To: Blank</div>
          <div>Status: Failed</div>
          <div>Message: {metadata?.body}</div>
        </div>
        )}
        {act.name === ACTIVITY_NAMES.LETTER_FAILED && <MessagePreviewItem activity={act} />}

        {act.name === ACTIVITY_NAMES.SENT_LETTER && act.letterJob && (
          <div>
            <span>
              Status: <b>{capitalize(act.letterJob.status ?? "")}</b>
            </span>
            {(act.letterJob.status === "sent" ||
              act.letterJob.status === "processing") && (
              <div>
                <a
                  target="_blank"
                  href={`/downloads/letter/${act.letterJob.lobLetterId}`} rel="noreferrer"
                >
                    View/Download Letter
                </a>
              </div>
            )}

            {act.letterJob.status === "error" && (
              <span>Error message: #{act.letterJob.errorMessage}</span>
            )}
          </div>
        )}

        {act.name === ACTIVITY_NAMES.SENT_LETTER && !act.letterJob && (
          <div>
            <LetterFeedItem activity={act} />
          </div>
        )}

        {metadata?.activityContent && <div>{metadata.activityContent}</div>}

        <div style={styles.date}>
          {moment(`${act.insertedAt}Z`).format("MMM Do YYYY, h:mm a")}
          {act.authorFullName && `  -  ${act.authorFullName}`}
        </div>
      </div>
    </div>
  );
};

export default FeedItem;
